import { API_BASE_URL, apiRequest, ensureAccessToken } from './apiClient';

const ADMIN_SESSION_KEY = 'fp:adminDashboardSession';

export const getAdminSessionToken = () => {
  try { return sessionStorage.getItem(ADMIN_SESSION_KEY) || ''; } catch { return ''; }
};

export const saveAdminSessionToken = (token) => {
  try {
    if (token) sessionStorage.setItem(ADMIN_SESSION_KEY, token);
  } catch {}
};

export const clearAdminSessionToken = () => {
  try { sessionStorage.removeItem(ADMIN_SESSION_KEY); } catch {}
};

const withAdminSession = (options = {}) => {
  const token = getAdminSessionToken();
  return {
    ...options,
    headers: {
      ...(options.headers || {}),
      ...(token ? { 'X-Admin-Session': token } : {}),
    },
  };
};

export const getAdminSessionStatus = async () => {
  const data = await apiRequest('/admin/session/status', withAdminSession());
  return data;
};

export const unlockAdminDashboard = async (password) => {
  const token = await ensureAccessToken({ source: 'admin_unlock' });
  const res = await fetch(`${API_BASE_URL}/admin/session/unlock`, {
    method: 'POST',
    cache: 'no-store',
    headers: {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ password: String(password ?? '') }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.ok === false) {
    const err = new Error(data?.message || 'Hatalı giriş.');
    err.code = data?.code || null;
    err.status = res.status;
    err.payload = data;
    throw err;
  }
  if (data.adminSessionToken) saveAdminSessionToken(data.adminSessionToken);
  return data;
};

export const lockAdminDashboard = async () => {
  try {
    await apiRequest('/admin/session/lock', withAdminSession({ method: 'POST' }));
  } finally {
    clearAdminSessionToken();
  }
};

export const getAdminDashboardSummary = async () => {
  const data = await apiRequest('/admin/dashboard/summary', withAdminSession());
  return data;
};

const withTrafficAccess = (managementContext, options = {}) => (
  managementContext
    ? {
      ...options,
      headers: {
        ...(options.headers || {}),
        'X-Admin-Management-Context': 'user-management',
      },
    }
    : withAdminSession(options)
);

export const getCorporateTrafficReport = async (days = 1, growthDays = 1, { managementContext = false, date = '' } = {}) => {
  const safeDays = [1, 7, 30, 90].includes(Number(days)) ? Number(days) : 1;
  const safeGrowthDays = [1, 7, 30].includes(Number(growthDays)) ? Number(growthDays) : 1;
  const safeDate = /^\d{4}-\d{2}-\d{2}$/.test(String(date || '')) ? String(date) : '';
  const params = new URLSearchParams({
    days: String(safeDays),
    growthDays: String(safeGrowthDays),
  });
  if (safeDate) params.set('date', safeDate);
  return apiRequest(
    `/admin/dashboard/site-traffic?${params.toString()}`,
    withTrafficAccess(managementContext)
  );
};

export const getAdminPerformanceOverview = async (range = '1h', { managementContext = false } = {}) => {
  const safeRange = ['15m', '1h', '6h', '24h', '7d', '30d'].includes(String(range)) ? String(range) : '1h';
  const data = await apiRequest(
    `/admin/dashboard/performance?range=${encodeURIComponent(safeRange)}`,
    withTrafficAccess(managementContext)
  );
  return data.overview;
};

export const refreshAppStoreTrafficReport = async ({ managementContext = false } = {}) => (
  apiRequest(
    '/admin/dashboard/site-traffic/app-store/refresh',
    withTrafficAccess(managementContext, { method: 'POST' })
  )
);

export const refreshMarketingTrafficReport = async (days = 14, { managementContext = false, date = '' } = {}) => (
  apiRequest(
    '/admin/dashboard/site-traffic/marketing/refresh',
    withTrafficAccess(managementContext, {
      method: 'POST',
      body: JSON.stringify({ days, ...(date ? { date } : {}) }),
    })
  )
);

export const refreshAdminStats = async () => {
  const data = await apiRequest('/admin/dashboard/refresh-stats', withAdminSession({ method: 'POST' }));
  return data;
};

export const getAdminStatsSnapshot = async (snapshotId) => {
  const data = await apiRequest(`/admin/dashboard/stats/${encodeURIComponent(snapshotId)}`, withAdminSession());
  return data;
};

export const createAdminExport = async (type = 'safe') => {
  const data = await apiRequest('/admin/export/create', withAdminSession({
    method: 'POST',
    body: JSON.stringify({ type }),
  }));
  return data;
};

const feedbackQuery = (filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      params.set(key, String(value));
    }
  });
  const query = params.toString();
  return query ? `?${query}` : "";
};

export const getAdminFeedbacks = async (filters = {}) => {
  return apiRequest(`/admin/feedbacks${feedbackQuery(filters)}`, withAdminSession());
};

export const updateAdminFeedbackStatus = async (feedbackId, status) => {
  return apiRequest(`/admin/feedbacks/${encodeURIComponent(feedbackId)}/status`, withAdminSession({
    method: "PATCH",
    body: JSON.stringify({ status }),
  }));
};

export const getAdminAccountDeletionSurveys = async (filters = {}, { managementContext = false } = {}) => {
  return apiRequest(
    `/admin/account-deletion-surveys${feedbackQuery(filters)}`,
    withTrafficAccess(managementContext)
  );
};

export const deleteAdminAccountDeletionSurvey = async (surveyId, { managementContext = false } = {}) => {
  return apiRequest(
    `/admin/account-deletion-surveys/${encodeURIComponent(surveyId)}`,
    withTrafficAccess(managementContext, { method: "DELETE" })
  );
};

async function adminBlobRequest(path) {
  const token = await ensureAccessToken({ source: 'admin_download' });
  const adminSession = getAdminSessionToken();
  const response = await fetch(`${API_BASE_URL}${path}`, {
    cache: "no-store",
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(adminSession ? { "X-Admin-Session": adminSession } : {}),
    },
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const error = new Error(data?.message || "Dosya alınamadı.");
    error.status = response.status;
    error.code = data?.code || null;
    throw error;
  }
  return response.blob();
}

export const getAdminFeedbackAttachment = async (attachmentId) => {
  return adminBlobRequest(`/admin/feedback-attachments/${encodeURIComponent(attachmentId)}`);
};

export const exportAdminFeedbackCsv = async (filters = {}) => {
  return adminBlobRequest(`/admin/feedbacks-export.csv${feedbackQuery(filters)}`);
};

export const downloadAdminExport = async (exportId) => {
  return adminBlobRequest(`/admin/export/${encodeURIComponent(exportId)}/download`);
};
